// Goal-Based Risk Alignment Tool — main page.
//
// Mounted at /dashboard/goal-based-risk-alignment-tool. The page:
//   1. Reads the user's persisted recommended allocation from
//      /assessment/results. Without one it bounces the user into
//      the Risk Assessment questionnaire (same UX as the Stress
//      Test page).
//   2. Loads the scenario catalogue from
//      GET /goal-alignment/scenarios.
//   3. Lets the user dial in a monthly contribution, time horizon,
//      goal target, scenario and shock-application mode (range
//      sliders + a dropdown).
//   4. Calls POST /goal-alignment/goal-simulation on every change
//      (debounced 300 ms) and renders:
//        - "Today's Risk Snapshot" card,
//        - the headline KPIs (success probability, median, p10, p90),
//        - the Recharts outcome distribution histogram,
//        - the scenario comparison table (Base case / Inflation /
//          Conflict by default).
//
// Visual design intentionally mirrors the Risk Assessment + Portfolio
// Stress Test screens (surface-card, page-title / subtitle, slate /
// emerald palette) so the suite feels like one product.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { riskApi } from '../risk-assessment/api/riskApi';
import OutcomeHistogram from './components/OutcomeHistogram';
import RiskSnapshotCard from './components/RiskSnapshotCard';
import ScenarioComparisonTable from './components/ScenarioComparisonTable';
import {
    goalAlignmentApi,
    type GoalScenarioCatalogue,
    type GoalSimulationResponse,
    type ScenarioComparisonRow,
    type ShockMode,
} from './api/goalAlignmentApi';

// ---------------------------------------------------------------------------
// Inline icons -- kept dependency-free, mirroring the rest of the suite.
// ---------------------------------------------------------------------------
const TargetIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-7 w-7"
        aria-hidden
    >
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden
    >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

const InfoIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden
    >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
    </svg>
);

// ---------------------------------------------------------------------------
// State machine + constants
// ---------------------------------------------------------------------------
type BootState =
    | { kind: 'loading' }
    | { kind: 'no-allocation' }
    | {
          kind: 'ready';
          allocation: Record<string, number>;
          riskProfile: string | null;
          scenarios: GoalScenarioCatalogue;
      }
    | { kind: 'error'; message: string };

// Slider ranges. Kept at the module level so the labels and request
// validation stay in sync with the backend schema (years 1..50).
const MONTHLY_MIN = 50;
const MONTHLY_MAX = 5_000;
const MONTHLY_STEP = 50;
const MONTHLY_DEFAULT = 500;

const YEARS_MIN = 1;
const YEARS_MAX = 50;
const YEARS_DEFAULT = 20;

// Goal target slider: piecewise stops so the user can move quickly
// from $10k to $5M without dragging through millions of irrelevant
// cents. Pattern is identical to the Stress Test page.
const TARGET_STOPS: readonly number[] = (() => {
    const stops: number[] = [];
    for (let v = 10_000; v < 100_000; v += 10_000) stops.push(v);
    for (let v = 100_000; v < 1_000_000; v += 50_000) stops.push(v);
    for (let v = 1_000_000; v <= 5_000_000; v += 250_000) stops.push(v);
    return stops;
})();
const TARGET_DEFAULT = 250_000;

function targetToIndex(amount: number): number {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < TARGET_STOPS.length; i++) {
        const d = Math.abs(TARGET_STOPS[i] - amount);
        if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
        }
    }
    return bestIdx;
}

// Initial-investment slider: piecewise stops (same idea as the goal
// target) so the user can dial a lump sum from $0 to $5M smoothly.
const INITIAL_STOPS: readonly number[] = (() => {
    const stops: number[] = [0];
    for (let v = 1_000; v < 10_000; v += 1_000) stops.push(v);
    for (let v = 10_000; v < 100_000; v += 10_000) stops.push(v);
    for (let v = 100_000; v < 1_000_000; v += 50_000) stops.push(v);
    for (let v = 1_000_000; v <= 5_000_000; v += 250_000) stops.push(v);
    return stops;
})();
const INITIAL_DEFAULT = 10_000;

function initialToIndex(amount: number): number {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < INITIAL_STOPS.length; i++) {
        const d = Math.abs(INITIAL_STOPS[i] - amount);
        if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
        }
    }
    return bestIdx;
}

// Debounce window for /goal-simulation calls. 300 ms per the product
// spec — short enough to feel live, long enough to not fire on every
// pixel of slider drag.
const SIMULATION_DEBOUNCE_MS = 300;

// Iterations used for the comparison-table rows specifically.
//
// The headline ``/goal-simulation`` call uses the engine default of
// 1 000 paths so the histogram looks smooth. The comparison endpoint
// runs N+1 simulations in series (base case + every named catalogue
// scenario) and gets re-fired on every contribution / horizon /
// target slider drag, so we lean on a slightly cheaper iteration
// count to keep the round-trip under ~150 ms even with 16 rows. 500
// iterations is more than enough for percentile estimates that only
// need to be accurate to the nearest 1 % of success probability.
const COMPARISON_ITERATIONS = 500;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatMoney(value: number): string {
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}

function formatMoneyCompact(value: number): string {
    const sign = value < 0 ? '-' : '';
    const v = Math.abs(value);
    if (v >= 1_000_000) return `${sign}$${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${sign}$${(v / 1_000).toFixed(0)}k`;
    return `${sign}$${v.toFixed(0)}`;
}

function extractAllocation(
    persisted: Awaited<ReturnType<typeof riskApi.getLatestResults>>,
): Record<string, number> | null {
    if (!persisted) return null;
    const allocation = persisted.portfolio?.allocation;
    if (!allocation || Object.keys(allocation).length === 0) return null;
    return allocation;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const NoAllocationPanel = () => (
    <section className="surface-card max-w-2xl">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">
            <InfoIcon />
        </div>
        <h2 className="section-title">Take the Risk Assessment first</h2>
        <p className="mt-3 text-sm muted">
            The Goal-Based Risk Alignment Tool simulates outcomes against your{' '}
            <strong className="text-slate-900 dark:text-white">
                recommended asset allocation
            </strong>
            . That allocation is produced by the Risk Intelligence Suite from
            your answers to the 43-question profiler. Once you've completed
            it we'll plug your allocation straight into this simulator.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
            <Link
                to="/dashboard/risk-assessment/test"
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-slate-200 px-4 py-2.5 text-sm font-bold text-white dark:text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
                Take the Risk Assessment <ArrowRightIcon />
            </Link>
            <Link
                to="/dashboard/risk-assessment"
                className="inline-flex items-center justify-center rounded-xl border-2 border-slate-300 dark:border-slate-700 px-4 py-2.5 text-sm font-bold text-slate-900 dark:text-white transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
            >
                Back to Risk Intelligence Suite
            </Link>
        </div>
    </section>
);

interface RangeFieldProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    format: (v: number) => string;
    onChange: (next: number) => void;
}

const RangeField = ({
    label,
    value,
    min,
    max,
    step = 1,
    format,
    onChange,
}: RangeFieldProps) => {
    const pct = ((value - min) / (max - min)) * 100;
    return (
        <label className="block">
            <div className="flex items-baseline justify-between">
                <span className="text-xs font-bold uppercase tracking-wide muted">
                    {label}
                </span>
                <span className="text-base font-black tabular-nums text-slate-900 dark:text-white">
                    {format(value)}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="mt-2 w-full appearance-none h-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                style={{
                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${pct}%, rgba(148,163,184,0.35) ${pct}%, rgba(148,163,184,0.35) 100%)`,
                }}
            />
            <div className="mt-1 flex justify-between text-xs muted tabular-nums">
                <span>{format(min)}</span>
                <span>{format(max)}</span>
            </div>
        </label>
    );
};

interface ScenarioControlProps {
    scenarios: string[];
    selectedScenario: string;
    onScenarioChange: (next: string) => void;
    shockMode: ShockMode;
    onShockModeChange: (next: ShockMode) => void;
}

const ScenarioControl = ({
    scenarios,
    selectedScenario,
    onScenarioChange,
    shockMode,
    onShockModeChange,
}: ScenarioControlProps) => {
    const isRandom = selectedScenario === 'random';
    const isBase = selectedScenario === '';
    return (
        <div className="space-y-3">
            <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide muted">
                    Scenario
                </span>
                <div className="relative mt-2">
                    <select
                        value={selectedScenario}
                        onChange={(e) => onScenarioChange(e.target.value)}
                        className="w-full appearance-none rounded-xl border-2 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/60 px-4 py-3 pr-10 text-sm font-semibold text-slate-900 dark:text-white shadow-sm transition-colors hover:border-emerald-400 dark:hover:border-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                    >
                        <option value="">Base case (no shock)</option>
                        <option value="random">Random shock per path</option>
                        <optgroup label="Specific scenarios">
                            {scenarios.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </optgroup>
                    </select>
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500"
                        aria-hidden
                    >
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </div>
            </label>

            {/* Shock-mode picker is only meaningful for a *specific*
                scenario. Hidden for the base case (no shock to apply)
                and for the random catalogue-sampling mode (which
                forces every_year semantics on the backend). */}
            {!isBase && !isRandom && (
                <fieldset className="rounded-xl border border-slate-300 dark:border-slate-700 px-3 py-2">
                    <legend className="px-1 text-xs font-bold uppercase tracking-wide muted">
                        Shock timing
                    </legend>
                    <div className="flex flex-wrap gap-2">
                        {(
                            [
                                { id: 'every_year', label: 'Every year' },
                                { id: 'shock_year', label: 'Single shock year' },
                            ] as { id: ShockMode; label: string }[]
                        ).map((opt) => {
                            const active = shockMode === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => onShockModeChange(opt.id)}
                                    className={[
                                        'rounded-lg px-3 py-1.5 text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                                        active
                                            ? 'bg-emerald-500 text-white shadow'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700',
                                    ].join(' ')}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>
            )}
        </div>
    );
};

// Map each canonical asset-class label (as returned by the backend in the
// recommended allocation) to its dedicated ranking page. Clicking an
// allocation pill deep-links the user straight into the corresponding
// asset-class leaderboard, tightening the loop between the goal-alignment
// planner and the per-asset intelligence pages. "Cash & Savings Accounts"
// has no ranking page, so it is intentionally omitted (stays static).
const ASSET_RANKING_ROUTES: Record<string, string> = {
    'Equities (Stocks)': '/dashboard/stock-ranking',
    'ETFs & Treasuries': '/dashboard/etf-ranking',
    Commodities: '/dashboard/commodities-ranking',
    REITs: '/dashboard/reits-ranking',
    Cryptocurrencies: '/dashboard/crypto-ranking',
    'Foreign Exchange (FX & Global Exposure)': '/dashboard/fx-ranking',
};

interface AllocationPillsProps {
    allocation: Record<string, number>;
}

const AllocationPills = ({ allocation }: AllocationPillsProps) => {
    const entries = Object.entries(allocation).sort((a, b) => b[1] - a[1]);
    return (
        <div className="flex flex-wrap gap-1.5">
            {entries.map(([asset, weight]) => {
                const rankingRoute = ASSET_RANKING_ROUTES[asset];
                const weightBadge = (
                    <span className="text-emerald-600 dark:text-emerald-400 font-black tabular-nums">
                        {weight.toFixed(0)}%
                    </span>
                );
                if (rankingRoute) {
                    return (
                        <Link
                            key={asset}
                            to={rankingRoute}
                            title={`View ${asset} ranking — ${weight.toFixed(1)}%`}
                            className="group inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        >
                            <span className="font-semibold text-slate-700 group-hover:text-emerald-600 dark:text-slate-200 dark:group-hover:text-emerald-400 transition-colors">
                                {asset}
                            </span>
                            {weightBadge}
                            <svg
                                aria-hidden
                                className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-emerald-500 transition-colors"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </Link>
                    );
                }
                return (
                    <span
                        key={asset}
                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs"
                        title={`${asset}: ${weight.toFixed(1)}%`}
                    >
                        <span className="font-semibold text-slate-700 dark:text-slate-200">
                            {asset}
                        </span>
                        {weightBadge}
                    </span>
                );
            })}
        </div>
    );
};

interface KpiBarProps {
    result: GoalSimulationResponse;
    viewMode: 'before' | 'after';
}

// Shows the KPIs for the ACTIVE reality only (Before Tax = gross, or
// After Tax & Fees = net). The reality is chosen by the toggle above so
// each card reflects exactly the selected view with no ambiguity.
const KpiBar = ({ result, viewMode }: KpiBarProps) => {
    const active = viewMode === 'after' ? result.after_tax : result.before_tax;

    const prob = active.probability_of_success;
    const probTone =
        prob > 0.75
            ? 'text-emerald-600 dark:text-emerald-400'
            : prob > 0.5
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-red-600 dark:text-red-400';
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border-2 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide muted">
                    Probability of success
                </p>
                <p className={`mt-1 text-2xl font-black tabular-nums ${probTone}`}>
                    {(prob * 100).toFixed(0)}%
                </p>
            </div>
            <div className="rounded-xl border-2 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide muted">
                    Median outcome
                </p>
                <p className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-white">
                    {formatMoneyCompact(active.median)}
                </p>
            </div>
            <div className="rounded-xl border-2 border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide muted">
                    Worst case (p10)
                </p>
                <p className="mt-1 text-2xl font-black tabular-nums text-red-600 dark:text-red-400">
                    {formatMoneyCompact(active.p10)}
                </p>
            </div>
            <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide muted">
                    Best case (p90)
                </p>
                <p className="mt-1 text-2xl font-black tabular-nums text-emerald-600 dark:text-emerald-400">
                    {formatMoneyCompact(active.p90)}
                </p>
            </div>
        </div>
    );
};

interface FeedbackBannerProps {
    feedback: GoalSimulationResponse['feedback'];
}

const FeedbackBanner = ({ feedback }: FeedbackBannerProps) => {
    const palette =
        feedback.level === 'strong'
            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-900 dark:text-emerald-100'
            : feedback.level === 'on-track'
            ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-100'
            : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-900 dark:text-red-100';
    return (
        <div className={`rounded-xl border-2 px-4 py-3 ${palette}`}>
            <p className="text-sm font-black uppercase tracking-wide">
                {feedback.headline}
            </p>
            <p className="mt-1 text-sm">{feedback.message}</p>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const GoalAlignmentPage = () => {
    const [boot, setBoot] = useState<BootState>({ kind: 'loading' });

    // Which dual-path reality is currently displayed across the whole
    // page. Defaults to the realistic After-Tax (net) view.
    const [viewMode, setViewMode] = useState<'before' | 'after'>('after');

    // User inputs.
    const [monthly, setMonthly] = useState<number>(MONTHLY_DEFAULT);
    const [years, setYears] = useState<number>(YEARS_DEFAULT);
    const [target, setTarget] = useState<number>(TARGET_DEFAULT);
    // ``''`` = base case, ``'random'`` = catalogue sampling, otherwise
    // the literal scenario name.
    const [scenario, setScenario] = useState<string>('');
    const [shockMode, setShockMode] = useState<ShockMode>('every_year');

    // Advanced inputs: taxes, fees & capital (drive the After-Tax path).
    const [initialInvestment, setInitialInvestment] =
        useState<number>(INITIAL_DEFAULT);
    const [tradingFee, setTradingFee] = useState<number>(0.0);
    const [dividendYield, setDividendYield] = useState<number>(0.0);
    const [dividendPeriod, setDividendPeriod] = useState<string>('annually');
    const [taxRate, setTaxRate] = useState<number>(0.0);

    // Live result.
    const [result, setResult] = useState<GoalSimulationResponse | null>(null);
    const [resultError, setResultError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);

    // Scenario comparison rows (computed lazily after the main run).
    const [comparison, setComparison] = useState<ScenarioComparisonRow[]>([]);
    const [comparisonRunning, setComparisonRunning] = useState(false);

    // Boot: load risk-assessment + scenarios in parallel.
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            riskApi.getLatestResults(),
            goalAlignmentApi.getScenarios(),
        ])
            .then(([persisted, scenarios]) => {
                if (cancelled) return;
                const allocation = extractAllocation(persisted);
                if (!allocation) {
                    setBoot({ kind: 'no-allocation' });
                    return;
                }
                setBoot({
                    kind: 'ready',
                    allocation,
                    riskProfile: persisted?.risk_profile ?? null,
                    scenarios: scenarios.scenarios,
                });
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('[GoalAlignmentPage] boot failed', err);
                setBoot({
                    kind: 'error',
                    message:
                        'We could not load the goal-alignment simulator. ' +
                        'Refresh the page or come back in a moment.',
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Debounced /goal-simulation runner.
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const runSimulation = useCallback(
        (
            allocation: Record<string, number>,
            scenarioName: string,
            mode: ShockMode,
            monthlyContribution: number,
            horizonYears: number,
            goalTarget: number,
            initInv: number,
            fee: number,
            divYield: number,
            divPeriod: string,
            tax: number,
        ) => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(async () => {
                setRunning(true);
                setResultError(null);
                try {
                    const data = await goalAlignmentApi.runSimulation({
                        allocation,
                        monthly_contribution: monthlyContribution,
                        years: horizonYears,
                        target: goalTarget,
                        scenario: scenarioName === '' ? null : scenarioName,
                        shock_mode:
                            scenarioName === ''
                                ? 'none'
                                : scenarioName === 'random'
                                ? 'every_year'
                                : mode,
                        initial_investment: initInv,
                        trading_fee_pct: fee,
                        dividend_yield_pct: divYield,
                        dividend_period: divPeriod,
                        capital_gain_tax_pct: tax,
                    });
                    setResult(data);
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[GoalAlignmentPage] run failed', err);
                    setResultError(
                        'The simulator could not compute that combination. ' +
                            'Try a different scenario or refresh.',
                    );
                } finally {
                    setRunning(false);
                }
            }, SIMULATION_DEBOUNCE_MS);
        },
        [],
    );

    // Re-run whenever any input (or the boot allocation) changes. This
    // is the auto re-evaluation trigger from the spec — allocation,
    // contribution, horizon, target, scenario or shock mode changing
    // all force a fresh simulation.
    useEffect(() => {
        if (boot.kind !== 'ready') return;
        runSimulation(
            boot.allocation,
            scenario,
            shockMode,
            monthly,
            years,
            target,
            initialInvestment,
            tradingFee,
            dividendYield,
            dividendPeriod,
            taxRate,
        );
    }, [
        boot,
        scenario,
        shockMode,
        monthly,
        years,
        target,
        initialInvestment,
        tradingFee,
        dividendYield,
        dividendPeriod,
        taxRate,
        runSimulation,
    ]);

    // Side comparison — runs the FULL catalogue (base case + every
    // named scenario from GET /goal-alignment/scenarios) whenever the
    // headline inputs change. Stays independent of the scenario
    // dropdown above the histogram, so the user always has a complete
    // reference table on screen no matter which scenario the
    // headline chart is currently showing.
    //
    // Why the full catalogue instead of a curated short-list: the
    // table is the place users come to ASK "which macro regime hurts
    // me most?". Hiding 12 of the 15 scenarios behind an additional
    // click would defeat the point. Performance is fine — 16 rows at
    // 500 iterations apiece on the vectorised numpy engine completes
    // in well under 150 ms.
    useEffect(() => {
        if (boot.kind !== 'ready') return;
        let cancelled = false;
        setComparisonRunning(true);
        // null prepended so the no-shock base case is always the
        // first row and the user can read the rest as deltas off it.
        const scenariosToCompare: (string | null)[] = [
            null,
            ...Object.keys(boot.scenarios),
        ];
        goalAlignmentApi
            .compareScenarios({
                allocation: boot.allocation,
                monthly_contribution: monthly,
                years,
                target,
                scenarios: scenariosToCompare,
                shock_mode: 'every_year',
                iterations: COMPARISON_ITERATIONS,
                initial_investment: initialInvestment,
                trading_fee_pct: tradingFee,
                dividend_yield_pct: dividendYield,
                dividend_period: dividendPeriod,
                capital_gain_tax_pct: taxRate,
            })
            .then((res) => {
                if (cancelled) return;
                setComparison(res.rows);
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.warn(
                    '[GoalAlignmentPage] comparison failed (non-fatal)',
                    err,
                );
                setComparison([]);
            })
            .finally(() => {
                if (!cancelled) setComparisonRunning(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        boot,
        monthly,
        years,
        target,
        initialInvestment,
        tradingFee,
        dividendYield,
        dividendPeriod,
        taxRate,
    ]);

    const scenarioNames = useMemo(
        () => (boot.kind === 'ready' ? Object.keys(boot.scenarios) : []),
        [boot],
    );

    // -------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------
    if (boot.kind === 'loading') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Goal-Based Risk Alignment Tool"
                subtitle="Loading your recommended allocation…"
            >
                <div className="surface-card-skeleton">
                    <div className="skeleton-line-lg w-1/3 mb-4" />
                    <div className="skeleton-line w-full mb-2" />
                    <div className="skeleton-line w-2/3" />
                </div>
            </Layout>
        );
    }

    if (boot.kind === 'error') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Goal-Based Risk Alignment Tool"
                subtitle="Something went wrong."
            >
                <p className="surface-card-error">{boot.message}</p>
                <Link
                    to="/dashboard/risk-assessment"
                    className="mt-4 inline-block text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                    ← Back to Risk Intelligence Suite
                </Link>
            </Layout>
        );
    }

    if (boot.kind === 'no-allocation') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Goal-Based Risk Alignment Tool"
                subtitle="See whether your allocation can hit the goal you have in mind."
            >
                <NoAllocationPanel />
            </Layout>
        );
    }

    // The outcome distribution currently on screen (Before or After Tax).
    const activeMetrics = result
        ? viewMode === 'after'
            ? result.after_tax
            : result.before_tax
        : null;

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Goal-Based Risk Alignment Tool"
            subtitle="Stress-test whether your recommended allocation can hit the goal you set, across thousands of simulated paths."
        >
            <div className="mb-4">
                <Link
                    to="/dashboard/risk-assessment"
                    className="text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400"
                >
                    ← Risk Intelligence Suite
                </Link>
            </div>

            {/* Today's Risk Snapshot — always visible at the top. */}
            {result && activeMetrics && (
                <div className="mb-6">
                    <RiskSnapshotCard
                        snapshot={activeMetrics}
                        note={
                            (scenario === ''
                                ? 'Base case'
                                : scenario === 'random'
                                ? 'Random shock per path'
                                : scenario) +
                            ` · ${result.iterations} simulated paths (${
                                viewMode === 'after' ? 'Net' : 'Gross'
                            })`
                        }
                    />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ---- Inputs --------------------------------------- */}
                <section className="lg:col-span-1 surface-card flex flex-col gap-5">
                    <div>
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <TargetIcon />
                        </div>
                        <h2 className="section-title">Plan inputs</h2>
                        <p className="mt-2 text-sm muted">
                            Set the goal, contribution and horizon. The
                            histogram and KPIs update live as you move the
                            sliders.
                        </p>
                    </div>

                    <RangeField
                        label="Monthly contribution"
                        value={monthly}
                        min={MONTHLY_MIN}
                        max={MONTHLY_MAX}
                        step={MONTHLY_STEP}
                        format={(v) => formatMoney(v)}
                        onChange={setMonthly}
                    />

                    <RangeField
                        label="Time horizon (years)"
                        value={years}
                        min={YEARS_MIN}
                        max={YEARS_MAX}
                        step={1}
                        format={(v) => `${v} yr${v === 1 ? '' : 's'}`}
                        onChange={setYears}
                    />

                    <RangeField
                        label="Goal target"
                        value={targetToIndex(target)}
                        min={0}
                        max={TARGET_STOPS.length - 1}
                        step={1}
                        format={(idx) =>
                            formatMoneyCompact(
                                TARGET_STOPS[idx] ?? TARGET_DEFAULT,
                            )
                        }
                        onChange={(idx) =>
                            setTarget(TARGET_STOPS[idx] ?? TARGET_DEFAULT)
                        }
                    />

                    <ScenarioControl
                        scenarios={scenarioNames}
                        selectedScenario={scenario}
                        onScenarioChange={setScenario}
                        shockMode={shockMode}
                        onShockModeChange={setShockMode}
                    />

                    <hr className="my-1 border-slate-200 dark:border-slate-800" />
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wide muted mb-4">
                            Taxes, Fees &amp; Capital
                        </h3>
                        <div className="space-y-4">
                            <RangeField
                                label="Initial investment"
                                value={initialToIndex(initialInvestment)}
                                min={0}
                                max={INITIAL_STOPS.length - 1}
                                step={1}
                                format={(idx) =>
                                    formatMoneyCompact(
                                        INITIAL_STOPS[idx] ?? INITIAL_DEFAULT,
                                    )
                                }
                                onChange={(idx) =>
                                    setInitialInvestment(
                                        INITIAL_STOPS[idx] ?? INITIAL_DEFAULT,
                                    )
                                }
                            />
                            <RangeField
                                label="Trading fee (% per year)"
                                value={tradingFee}
                                min={0}
                                max={5}
                                step={0.1}
                                format={(v) => `${v.toFixed(1)}%`}
                                onChange={setTradingFee}
                            />
                            <RangeField
                                label="Dividend yield (%)"
                                value={dividendYield}
                                min={0}
                                max={15}
                                step={0.1}
                                format={(v) => `${v.toFixed(1)}%`}
                                onChange={setDividendYield}
                            />

                            <label className="block">
                                <span className="text-xs font-bold uppercase tracking-wide muted">
                                    Dividend period
                                </span>
                                <div className="relative mt-2">
                                    <select
                                        value={dividendPeriod}
                                        onChange={(e) =>
                                            setDividendPeriod(e.target.value)
                                        }
                                        className="w-full appearance-none rounded-xl border-2 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/60 px-4 py-3 pr-10 text-sm font-semibold text-slate-900 dark:text-white shadow-sm transition-colors hover:border-emerald-400 dark:hover:border-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                                    >
                                        <option value="quarterly">
                                            Quarterly
                                        </option>
                                        <option value="semi-annually">
                                            Semi-annually
                                        </option>
                                        <option value="annually">
                                            Annually
                                        </option>
                                    </select>
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500"
                                        aria-hidden
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </div>
                            </label>

                            <RangeField
                                label="Capital gain tax (%)"
                                value={taxRate}
                                min={0}
                                max={50}
                                step={1}
                                format={(v) => `${v.toFixed(0)}%`}
                                onChange={setTaxRate}
                            />
                        </div>
                    </div>

                    <div className="mt-2 rounded-xl bg-slate-100 dark:bg-slate-800/60 p-3 text-xs muted space-y-2">
                        <p>
                            Profile in use:{' '}
                            <span className="font-bold text-slate-900 dark:text-white">
                                {boot.riskProfile ?? 'Latest recommendation'}
                            </span>
                        </p>
                        <AllocationPills allocation={boot.allocation} />
                    </div>
                </section>

                {/* ---- Results ------------------------------------- */}
                <section className="lg:col-span-2 surface-card">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div>
                            <h2 className="section-title">
                                {scenario === ''
                                    ? 'Base case'
                                    : scenario === 'random'
                                    ? 'Random shock per path'
                                    : scenario}
                            </h2>
                            <p className="text-sm muted mt-1">
                                {result
                                    ? `${result.iterations} simulated paths · contributing ${formatMoney(
                                          monthly,
                                      )}/mo for ${years} years toward ${formatMoneyCompact(
                                          target,
                                      )}.`
                                    : 'Computing your simulation…'}
                                {result?.shock_year != null && (
                                    <>
                                        {' '}
                                        Shock landed around year{' '}
                                        <strong>{result.shock_year}</strong>.
                                    </>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {running && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                    Running…
                                </span>
                            )}
                            {/* Toggle the entire page between the gross
                                (Before Tax) and net (After Tax & Fees)
                                realities. */}
                            <div className="inline-flex items-center rounded-lg bg-slate-200/60 dark:bg-slate-800 p-1">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('before')}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                                        viewMode === 'before'
                                            ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white'
                                            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    Before Tax
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('after')}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                                        viewMode === 'after'
                                            ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white'
                                            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    After Tax &amp; Fees
                                </button>
                            </div>
                        </div>
                    </div>

                    {resultError && (
                        <p className="surface-card-error mb-4">{resultError}</p>
                    )}

                    {result && activeMetrics ? (
                        <div className="space-y-6 animate-fade-slide-up">
                            <KpiBar result={result} viewMode={viewMode} />
                            <FeedbackBanner feedback={result.feedback} />
                            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                                <p className="mb-2 text-xs font-bold uppercase tracking-wide muted">
                                    Outcome distribution at year {result.years} (
                                    {viewMode === 'after' ? 'Net' : 'Gross'})
                                </p>
                                <OutcomeHistogram
                                    bins={activeMetrics.histogram}
                                    target={result.target}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="surface-card-skeleton">
                            <div className="skeleton-line-lg w-1/3 mb-4" />
                            <div className="skeleton-line w-full mb-2" />
                            <div className="skeleton-line w-2/3" />
                        </div>
                    )}
                </section>
            </div>

            {/* ---- Scenario comparison ------------------------------ */}
            <section className="surface-card mt-6 animate-fade-slide-up">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                        <h3 className="section-title">Scenario comparison</h3>
                        <p className="text-sm muted">
                            How your success probability bends under different
                            macro regimes — independent of the scenario picked
                            above.
                        </p>
                    </div>
                    {comparisonRunning && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-bold muted">
                            <span className="h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
                            Updating…
                        </span>
                    )}
                </div>
                {comparison.length > 0 ? (
                    <ScenarioComparisonTable
                        rows={comparison}
                        highlightScenario={
                            scenario === '' ? 'Base case' : scenario
                        }
                        viewMode={viewMode}
                    />
                ) : (
                    <p className="text-xs muted italic">
                        Comparison will appear here as soon as the inputs
                        settle.
                    </p>
                )}
            </section>
        </Layout>
    );
};

export default GoalAlignmentPage;
